import { formatFileSize } from "@/lib/formatFileSize";
import { useState, useCallback, useRef, useMemo } from "react";
import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useFreemium } from "@/hooks/useFreemium";
import { loadFFmpeg, fetchFile, checkFileSize, ffmpegOutputToBlob, resetFFmpegEngine } from "@/lib/ffmpegEngine";
import { calculateTargetBitrateKbps, compressAudioToBitrate, MIN_BITRATE_KBPS } from "./operations";
import { AudioProcessingProgress, showAudioError, FileSizeNotice } from "./uiHelpers";
import type { FFmpeg } from "@ffmpeg/ffmpeg";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Reduces an audio file's size by lowering its bitrate (down to 32 kbps), optionally converting to mono or aiming at an exact size in MB, useful for fitting an email attachment limit or saving storage space. Medium/Strong target a real reduction (roughly 25-30% / 50% smaller; the exact saving is shown after the run), calculated directly from the file's length — audio bitrate encoding is predictable enough that this doesn't need trial-and-error the way video compression does. Runs entirely in your browser — the audio never leaves your device.`,
  how: `Upload your audio file, pick Medium or Strong for a targeted reduction, or choose a specific bitrate manually if you want direct control.`,
  tip: `128 kbps is a reasonable, widely-used balance for spoken word and most music. Go lower (64-96 kbps) for voice memos or podcasts where small size matters more than audio fidelity, or higher (192-256 kbps) for music where quality matters more.`,
};

const BITRATE_OPTIONS = [32, 48, 64, 96, 128, 160, 192, 256];

type Mode = "medium" | "strong" | "bitrate" | "size";
// Computed directly from the file's own duration and size, not searched
// for -- MP3's bitrate-based encoding lands close enough to a requested
// bitrate that a single calculated pass is reliable, unlike video's
// quality-based (CRF) encoding, which genuinely needs the iterative
// search used in CompressVideo.
const PERCENT_TARGETS: Partial<Record<Mode, number>> = {
  medium: 0.75, // 25% reduction
  strong: 0.5, // 50% reduction
};

export default function AudioCompressor() {
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [sourceBitrateKbps, setSourceBitrateKbps] = useState<number | null>(null);
  const [durationSec, setDurationSec] = useState<number | null>(null);
  const [mode, setMode] = useState<Mode>("medium");
  const [bitrate, setBitrate] = useState([128]);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [result, setResult] = useState<{ blob: Blob; size: number } | null>(null);
  const [notSmaller, setNotSmaller] = useState(false);
  const [mono, setMono] = useState(false);
  const [targetMb, setTargetMb] = useState("5");
  const { isPro, isLoading, isAdmin } = useFreemium();
  const ffmpegRef = useRef<FFmpeg | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const resultUrl = useMemo(() => (result ? URL.createObjectURL(result.blob) : ""), [result]);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f);
    setPreviewUrl(URL.createObjectURL(f));
    setResult(null);
    setSourceBitrateKbps(null);
  }, []);

  const onLoadedMetadata = () => {
    const duration = audioRef.current?.duration;
    if (file && duration && Number.isFinite(duration) && duration > 0) {
      setDurationSec(duration);
      const effective = (file.size * 8) / duration / 1000;
      setSourceBitrateKbps(effective);
      // Default to a bitrate that's actually lower than the source,
      // rather than leaving the slider on a value that might not
      // compress anything for this specific file.
      const usableOptions = BITRATE_OPTIONS.filter(b => b < effective);
      if (usableOptions.length > 0 && bitrate[0] >= effective) {
        setBitrate([usableOptions[usableOptions.length - 1]]);
      }
    }
  };

  const sizeCheck = file ? checkFileSize([file], isPro || isLoading, isAdmin) : null;

  const stop = () => {
    abortRef.current?.abort();
    if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
  };

  const compress = async () => {
    if (!file) return;
    if ((mode !== "bitrate") && !durationSec) {
      toast.error("Still reading the audio file's length — try again in a moment");
      return;
    }
    setProcessing(true);
    setProgress(0);
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      setStatusMessage("Loading audio engine...");
      const ffmpeg = await loadFFmpeg((msg) => setStatusMessage(msg));
      ffmpegRef.current = ffmpeg;

      setStatusMessage("Reading audio file...");
      const ext = file.name.match(/\.([^.]+)$/)?.[1] || "mp3";
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(file));

      let effectiveBitrate = bitrate[0];
      if (mode === "size") {
        const tb = parseFloat(targetMb) * 1024 * 1024;
        if (!(tb > 0)) { toast.error("Enter a target size in MB"); setProcessing(false); return; }
        // 3% under the target, because MP3 frames and tags add a little to the raw bitrate.
        effectiveBitrate = Math.max(MIN_BITRATE_KBPS, Math.floor(((tb * 0.97 * 8) / (durationSec as number) / 1000) / (mono ? 1 : 1)));
        if ((tb * 0.97 * 8) / (durationSec as number) / 1000 < MIN_BITRATE_KBPS) toast.warning(`${targetMb} MB is below what ${MIN_BITRATE_KBPS} kbps allows for this length, so the file will come out a little larger than that.`);
      } else if (mode !== "bitrate") {
        // durationSec is guaranteed non-null here by the guard above.
        effectiveBitrate = calculateTargetBitrateKbps(file.size, PERCENT_TARGETS[mode] as number, durationSec as number);
      }

      setStatusMessage("Compressing...");
      const data = await compressAudioToBitrate(ffmpeg, `input.${ext}`, "output.mp3", effectiveBitrate, setProgress, controller.signal, mono);

      const blob = ffmpegOutputToBlob(data, "audio/mpeg");
      const change = ((1 - blob.size / file.size) * 100).toFixed(0);
      if (blob.size >= file.size) {
        // Never offer a "compressed" file that is not smaller (e.g. a 28 kbps Opus voice note re-encoded as MP3).
        setNotSmaller(true);
        toast.info("This file is already well compressed, so re-encoding would make it bigger. Keep the original.");
        return;
      }
      setNotSmaller(false);
      setResult({ blob, size: blob.size });
      const lossless = /\.(wav|flac|aiff?)$/i.test(file.name);
      if (lossless) {
        toast.success(`Reduced by ${change}%. ${/\.flac$/i.test(file.name) ? "FLAC" : "WAV"} is lossless or uncompressed, so MP3 is much smaller whatever the preset.`);
      } else if (mode === "size") {
        toast.success(`Compressed to ${formatFileSize(blob.size)} (target ${targetMb} MB).`);
      } else if (mode === "bitrate") {
        toast.success(Number(change) > 0 ? `Compressed! Reduced by ${change}%` : "Compressed, though this file was already efficiently encoded");
      } else {
        const label = mode === "medium" ? "25%" : "50%";
        toast.success(Number(change) >= Number(label.replace("%", "")) - 3
          ? `Compressed! Close to the ${label} reduction target.`
          : `Compressed by ${change}% — couldn't fully reach the ${label} target since that would need a bitrate below what still sounds reasonable (${MIN_BITRATE_KBPS} kbps floor).`);
      }
    } catch (err) {
      showAudioError(err);
    } finally {
      if (ffmpegRef.current) resetFFmpegEngine(ffmpegRef.current);
      setProcessing(false);
    }
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="audio/*" maxSize={500} onFiles={handleFiles} label="Drop an audio file to compress" description="Reduce audio file size" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card flex justify-between">
            <div><p className="text-sm font-medium">{file.name}</p><p className="text-xs text-muted-foreground">Original: {formatFileSize(file.size)}</p></div>
            {result && <p className="text-sm font-medium text-green-600">{formatFileSize(result.size)}</p>}
          </div>
          {sizeCheck && <FileSizeNotice check={sizeCheck} isPro={isPro} />}

          <audio ref={audioRef} src={previewUrl} onLoadedMetadata={onLoadedMetadata} className="hidden" />

          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant={mode === "medium" ? "default" : "outline"} onClick={() => { setMode("medium"); setResult(null); setNotSmaller(false); }}>Medium (about 25-30% smaller)</Button>
            <Button size="sm" variant={mode === "strong" ? "default" : "outline"} onClick={() => { setMode("strong"); setResult(null); setNotSmaller(false); }}>Strong (about 50% smaller)</Button>
            <Button size="sm" variant={mode === "bitrate" ? "default" : "outline"} onClick={() => { setMode("bitrate"); setResult(null); setNotSmaller(false); }}>Manual Bitrate</Button>
            <Button size="sm" variant={mode === "size" ? "default" : "outline"} onClick={() => { setMode("size"); setResult(null); setNotSmaller(false); }}>Target size</Button>
          </div>

          {mode === "size" ? (
            <div className="flex items-end gap-2">
              <div><Label className="text-xs">Target size (MB)</Label><input type="number" min="0.1" step="0.5" className="border rounded px-2 py-1 w-28 bg-background block" value={targetMb} onChange={(e) => { setTargetMb(e.target.value); setResult(null); }} /></div>
              {[2, 5, 10, 25].map((n) => <Button key={n} size="sm" variant="ghost" className="h-8 text-xs" onClick={() => { setTargetMb(String(n)); setResult(null); }}>{n} MB</Button>)}
            </div>
          ) : mode !== "bitrate" ? (
            <p className="text-xs text-muted-foreground">
              Aims for about {mode === "medium" ? "25-30%" : "50%"} smaller (the exact saving is shown after the run), calculated from this file's length — click Compress Audio below.
              {!durationSec && " (reading file length...)"}
            </p>
          ) : (
          <div className="space-y-2 max-w-sm">
            <Label>Bitrate: {bitrate[0]} kbps</Label>
            <Slider
              value={bitrate}
              onValueChange={(v) => { setBitrate(v); setResult(null); }}
              min={32}
              max={sourceBitrateKbps ? Math.max(64, Math.min(256, Math.floor(sourceBitrateKbps / 32) * 32)) : 256}
              step={32}
            />
            <div className="flex flex-wrap gap-1.5">
              {BITRATE_OPTIONS.map(b => {
                // Selecting a bitrate at or above what the file already
                // has doesn't compress anything -- it can't add detail
                // that wasn't there, and often makes the file larger
                // instead of smaller, which defeats the point of this
                // tool. Disabled rather than silently allowed.
                const disabled = sourceBitrateKbps !== null && b >= sourceBitrateKbps;
                return (
                  <Button
                    key={b}
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs"
                    disabled={disabled}
                    title={disabled ? "This is at or above the file's own bitrate — wouldn't reduce size" : undefined}
                    onClick={() => { setBitrate([b]); setResult(null); }}
                  >
                    {b}k
                  </Button>
                );
              })}
            </div>
            {sourceBitrateKbps !== null && (
              <p className="text-xs text-muted-foreground">
                This file is already encoded at roughly {Math.round(sourceBitrateKbps)} kbps — bitrates at or above that are grayed out above since they wouldn't actually reduce the file size.
              </p>
            )}
          </div>
          )}

          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={mono} onChange={(e) => { setMono(e.target.checked); setResult(null); }} /> Convert to mono (best for voice recordings; halves the audio data)</label>
          {processing && <AudioProcessingProgress progress={progress} statusMessage={statusMessage} onStop={stop} />}
          {notSmaller && !result && (
            <NoticeBox className="text-sm" testId="not-smaller">
              This file is already well compressed (for example an Opus voice note), so converting it to MP3 would make it bigger. Your original is the better file to keep.
            </NoticeBox>
          )}
          {result && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="ba-players">
              <div><p className="text-xs text-muted-foreground mb-1">Original ({formatFileSize(file.size)})</p><audio src={previewUrl} controls className="w-full" /></div>
              <div><p className="text-xs text-muted-foreground mb-1">Compressed ({formatFileSize(result.size)})</p><audio src={resultUrl} controls className="w-full" /></div>
            </div>
          )}
          <div className="flex gap-3">
            {result ? (
              <>
                <DownloadButton blob={result.blob} fileName={`compressed_${file.name.replace(/\.[^.]+$/, ".mp3")}`} />
                <Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={compress} disabled={processing || sizeCheck?.severity !== "ok" || (mode !== "bitrate" && !durationSec)}>{processing ? "Compressing..." : "Compress Audio"}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
